import React from 'react';
import * as THREE from 'three';
import GLTFLoader from 'three-gltf-loader';
import OrbitControls from 'three-orbitcontrols';
import { Grid, Typography } from '@mui/material';
import '../../../styles/canvas.css';

const size = {
    width: 430,
    height: 300,
};

export const BMIProfile = ({
    sex,
    status,
    height,
    weight,
    showBMI,
    classes,
    ...props
}) => {
    const [mounted, setMounted] = React.useState(false);
    const mount = React.useRef(null);
    React.useEffect(() => {
        if (!mounted && mount && showBMI) {
            const profileState = {
                male: {
                    avatar: null,
                    defaultHeight: null,
                    portly: null,
                    minHeight: 1.3,
                    maxHeight: 2,
                    minPortly: -1,
                    maxPortly: 1.5,
                },
                female: {
                    avatar: null,
                    defaultHeight: null,
                    portly: null,
                    minHeight: 1.3,
                    maxHeight: 2,
                    minPortly: -1,
                    maxPortly: 1.5,
                },
                isMaleVisible: sex === 'male',
            };

            const changeWeight = (value) => {
                const params = profileState.isMaleVisible
                    ? profileState.male
                    : profileState.female;

                let calculatedValue = 0;

                const calculateValue = (
                    value,
                    min,
                    max,
                    minTarget,
                    maxTarget
                ) =>
                    ((value - min) * (maxTarget - minTarget)) / (max - min) +
                    minTarget;
                if (value < 19) {
                    calculatedValue = calculateValue(value, 9, 19, -1, 0);
                } else if (value < 25) {
                    calculatedValue = calculateValue(value, 19, 25, 0, 0.75);
                } else if (value < 30) {
                    calculatedValue = calculateValue(value, 25, 30, 0.75, 1);
                } else if (value < 40) {
                    calculatedValue = calculateValue(value, 30, 40, 1, 1.25);
                } else {
                    calculatedValue = calculateValue(value, 40, 65, 1.25, 1.5);
                }

                if (
                    calculatedValue < params.minPortly ||
                    calculatedValue > params.maxPortly
                ) {
                    params.portly[0] =
                        value < params.minPortly
                            ? params.minPortly
                            : params.maxPortly;
                } else {
                    params.portly[0] = calculatedValue;
                }
            };

            // const changeHeight = (value) => {
            //   const params = profileState.isMaleVisible ? profileState.male : profileState.female;
            //   if (value < params.minHeight || value > params.maxHeight) return;
            //
            //   const { bones } = params.avatar.children[1].skeleton;
            //   const head = bones[91];
            //
            //   let height = value / params.defaultHeight;
            //
            //   if (value < params.defaultHeight - 0.4) height *= 0.975;
            //   else if (value < params.defaultHeight - 0.3) height *= 0.985;
            //   else if (value < params.defaultHeight - 0.2) height *= 0.995;
            //   if (value > params.defaultHeight) height /= 0.99;
            //
            //   params.avatar.parent.scale.set(height, height, height);
            //
            //   let headScale = params.defaultHeight / value;
            //   if (value < params.defaultHeight - 0.2) headScale *= 0.85;
            //   head.scale.set(headScale, headScale, headScale);
            // };

            let cameraShadow;
            let camera;
            let scene;
            let renderer;
            let controls;

            const init = () => {
                scene = new THREE.Scene();
                // scene.background = new THREE.Color(0x666666);

                camera = new THREE.PerspectiveCamera(
                    45,
                    window.innerWidth / window.innerHeight,
                    0.1,
                    100
                );
                // camera.position.set(0, 2, 3);
                camera.position.set(2, 1.7, 1.9);
                scene.add(camera);

                cameraShadow = new THREE.PerspectiveCamera(
                    50,
                    1, // window.innerWidth / window.innerHeight,
                    0.01,
                    10000 // ,3.5
                );
                cameraShadow.position.set(0, 3, 0);
                cameraShadow.lookAt(new THREE.Vector3());
                scene.add(cameraShadow);

                const spotLight = new THREE.SpotLight(0x888888);
                spotLight.angle = Math.PI / 5;
                spotLight.penumbra = 0.3;
                spotLight.position.set(0, 5, -0.1);
                spotLight.castShadow = true;
                spotLight.shadow.camera.near = 1;
                spotLight.shadow.camera.far = 12;
                spotLight.shadow.mapSize.width = 256 * 1.2;
                spotLight.shadow.mapSize.height = 256 * 1.2;
                spotLight.shadow.bias = -0.002;
                spotLight.shadow.radius = 9;
                scene.add(spotLight);

                renderer = new THREE.WebGLRenderer({
                    antialias: true,
                    alpha: true,
                });
                renderer.setPixelRatio(window.devicePixelRatio);
                // renderer.setSize((220 * window.innerWidth) / window.innerHeight, 300);
                renderer.setSize(size.width, size.height);
                // renderer.shadowMap.enabled = true;
                // renderer.outputEncoding = THREE.sRGBEncoding;
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.VSMShadowMap;

                document.body.appendChild(renderer.domElement);

                controls = new OrbitControls(camera, renderer.domElement);
                controls.target = new THREE.Vector3(0, 1, 0);
                controls.enableDamping = true;
                controls.autoRotate = true;
                controls.autoRotateSpeed = 5;
                controls.enableZoom = false;
                controls.dampingFactor = 0.05;
                camera.updateProjectionMatrix();

                const ground = new THREE.Mesh(
                    new THREE.PlaneBufferGeometry(2, 2, 2),
                    new THREE.ShadowMaterial({ opacity: 0.2 })
                );
                // ground.material.map = renderTarget.texture;
                ground.receiveShadow = true;
                ground.rotation.x = -Math.PI / 2;
                ground.frustumCulled = false;
                scene.add(ground);

                const loader = new GLTFLoader();
                const material = new THREE.MeshMatcapMaterial({
                    side: THREE.FrontSide,
                    morphTargets: true,
                    skinning: true,
                });

                const promises = [
                    new Promise((resolve, reject) => {
                        loadAvatar('/assetsMorph/male.glb', 'male', resolve);
                    }),
                    new Promise((resolve, reject) => {
                        loadAvatar(
                            '/assetsMorph/female.glb',
                            'female',
                            resolve
                        );
                    }),
                    new Promise((resolve) => {
                        new THREE.TextureLoader().load(
                            '/assetsMorph/mcwb.jpg',
                            resolve
                        );
                    }),
                ];

                Promise.all(promises)
                    .then((avatarsData) => {
                        profileState.male.avatar = avatarsData[0];
                        profileState.female.avatar = avatarsData[1];

                        const texture = avatarsData[2];
                        // @ts-ignore
                        texture.magFilter = THREE.LinearFilter;
                        // @ts-ignore
                        texture.minFilter = THREE.LinearFilter;
                        material.matcap = texture;
                        material.needsUpdate = true;

                        // 0.07 and 0.103 is coefficients, it brings with a last version of three.js gltf loader
                        profileState.male.defaultHeight =
                            calcSize(profileState.male.avatar) - 0.07;
                        profileState.female.defaultHeight =
                            calcSize(profileState.female.avatar) - 0.103;

                        const malegroup = new THREE.Group().add(
                            profileState.male.avatar
                        );
                        const femalegroup = new THREE.Group().add(
                            profileState.female.avatar
                        );

                        scene.add(malegroup);
                        scene.add(femalegroup);

                        // if (height !== -1) changeHeight(height);
                        if (weight !== -1) changeWeight(weight);

                        // @ts-ignore
                        window.male = malegroup;
                        // @ts-ignore
                        window.female = femalegroup;

                        function calcSize(group) {
                            const BoxHelper = new THREE.BoxHelper(group);
                            const box3 = new THREE.Box3();
                            const size = new THREE.Vector3();
                            box3.setFromObject(BoxHelper);
                            box3.getSize(size);
                            return size.z * 100;
                        }
                    })
                    .catch((error) => {
                        console.log('oh', error);
                    });

                function loadAvatar(url, gender, resolve) {
                    loader.load(url, (gltf) => {
                        const object = gltf.scene;

                        object.traverse((node) => {
                            if (node.isMesh) {
                                node.castShadow = true;
                                node.material = material;
                                node.frustumCulled = false;
                                node.material.needsUpdate = true;
                            }
                        });
                        const model = object.children[0];
                        setupDataMorphBody(model, gender);

                        if (gender !== sex) model.visible = false;
                        resolve(model);

                        function setupDataMorphBody(model, gender) {
                            profileState[gender].portly =
                                model.children[1].morphTargetInfluences;
                        }
                    });
                }

                window.addEventListener('resize', onWindowResize, false);
            };

            // @ts-ignore
            function onWindowResize() {
                renderer.setSize(size.width, size.height);
            }

            const animate = () => {
                requestAnimationFrame(animate);
                controls.update();
                renderer.render(scene, camera);
            };

            init();
            animate();

            // use ref as a mount point of the Three.js scene instead of the document.body
            renderer.domElement.id = 'bmiProfileCanvas';
            mount.current.appendChild(renderer.domElement);
            setMounted(true);
        }
    }, [height, mounted, sex, showBMI, weight]);

    if (!showBMI) return null;
    return (
        <Grid container justifyContent="center" spacing={1}>
            <Grid item xs={12}>
                <Typography variant="subtitle1" align="center">
                    BMI Simulation{' '}
                    <Typography color={'primary'}>"{status}"</Typography>
                </Typography>
            </Grid>
            <Grid item>
                <div ref={mount} />
            </Grid>
        </Grid>
    );
};

export default BMIProfile;
